import React, { useState } from 'react'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import VoteCellModal from 'components/spreadsheet/components/modal/views/vote'
import { IVote } from 'types'
import { useAuth } from 'hooks/auth'
import { Visible } from 'components/icons'
import { uuidv4 } from 'helpers/utils'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'

interface VoteProps {
  value: IVote[] | null
  align: 'center' | 'left' | 'right'
  color: string
  readOnly: boolean
  isAdmin: boolean
  isContributor: boolean
  width: number
  rowHeight: number
  selected: boolean
  rowNumber: number | undefined
  validation: boolean
  setCellValue: (value: IVote[], onSuccess: () => void, onError: () => void) => void
  isExpanded?: boolean
}

const styles = cellStyles()

const Vote: React.FC<VoteProps> = ({
  value,
  align,
  color,
  readOnly,
  isAdmin,
  isContributor,
  width,
  rowHeight,
  selected,
  rowNumber,
  validation,
  setCellValue,
  isExpanded
}) => {
  const votes = value && Array.isArray(value) ? value : []
  const [votesModal, setVotesModal] = useState<boolean>(false)
  const [processing, setProcessing] = useState<boolean>(false)
  const heightPx = getRowHeightVariable(rowHeight)

  const { user } = useAuth()

  const { positiveVotes, negativeVotes, alreadyVotedPositive, alreadyVotedNegative } = (() => {
    let positive = 0
    let negative = 0
    let votedPositive = false
    let votedNegative = false
    const userId = user?.firebaseUserId

    for (let i = 0; i < votes.length; i++) {
      const vote = votes[i]
      if (vote.type) {
        positive++
        if (userId && vote.firebaseUserId === userId) votedPositive = true
      } else {
        negative++
        if (userId && vote.firebaseUserId === userId) votedNegative = true
      }
    }

    return {
      positiveVotes: positive,
      negativeVotes: negative,
      alreadyVotedPositive: votedPositive,
      alreadyVotedNegative: votedNegative
    }
  })()
  const canVote = (isAdmin || isContributor) && !readOnly

  const handleVoteClick = (type: boolean) => {
    const newVotes = [...votes].filter((vote: IVote) => user && vote.firebaseUserId !== user!.firebaseUserId)

    if ((!alreadyVotedPositive && type) || (!alreadyVotedNegative && !type)) {
      newVotes.push({
        firebaseUserId: user ? user!.firebaseUserId : uuidv4(),
        type,
        timestamp: new Date().toISOString()
      })
    }

    if (processing) return
    setProcessing(true)
    setCellValue(
      newVotes,
      () => setProcessing(false),
      () => setProcessing(false)
    )
  }
  return (
    <div
      className={styles.value({ focused: selected, class: 'cell-value' })}
      data-focussed={selected || undefined}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        maxHeight: `${heightPx - 2}px`,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)'
      }}
      onDoubleClick={(event: React.MouseEvent) => {
        if (readOnly || isExpanded) {
          event.stopPropagation()
          return
        }
        event.stopPropagation()
      }}
    >
      <div
        className={styles.content({
          validation,
          class: 'flex items-center justify-start border-0 leading-normal'
        })}
        style={{
          height: selected ? heightPx - 3 : 'unset'
        }}
      >
        <span className="flex items-center gap-[5px]">
          <span className={`${selected && width < 112 ? 'mr-[10px]' : 'mr-0'}`}>
            <span
              className={canVote ? 'cursor-pointer' : 'cursor-default'}
              onClick={() => {
                if ((selected || isExpanded) && canVote) handleVoteClick(true)
              }}
            >
              👍
            </span>
            <span className={`font-semibold ml-[3px] ${alreadyVotedPositive ? 'text-blue' : 'text-inherit'}`}>
              {positiveVotes}
            </span>
          </span>
          <span className={`${selected && width < 112 ? 'mr-[10px]' : 'mr-0'}`}>
            <span
              className={canVote ? 'cursor-pointer' : 'cursor-default'}
              onClick={() => {
                if ((selected || isExpanded) && canVote) handleVoteClick(false)
              }}
            >
              👎
            </span>
            <span className={`font-semibold ml-[3px] ${alreadyVotedNegative ? 'text-blue' : 'text-inherit'}`}>
              {negativeVotes}
            </span>
          </span>
        </span>

        {(selected || isExpanded) && (
          <a
            href="#view-votes"
            aria-label="View votes"
            aria-haspopup="dialog"
            aria-controls="vote-cell-modal"
            className="flex items-center ml-auto"
            onClick={() => setVotesModal(true)}
          >
            <Visible className="h-6 w-6" />
          </a>
        )}
      </div>
      {votesModal && (
        <VoteCellModal
          id="vote-cell-modal"
          open={votesModal}
          setOpen={setVotesModal}
          votes={votes}
          rowNumber={rowNumber !== undefined ? rowNumber : -1}
        />
      )}
    </div>
  )
}

export const VoteCell = Vote
